'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import { PasswordField } from '@sola/ark-core/molecules/password-field/password-field.tsx';
import { PasswordRequirements } from '@sola/ark-core/molecules/password-requirements/password-requirements.tsx';
import { passwordValidator } from '@sola/ark-core/utils/helpers.ts';
import { usePasswordUpdate } from '@sola/my-sdk/settings/hooks.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  SettingsErrorAlert,
  createPasswordUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

const PasswordModalForm = ({
  currentPassword,
  setCurrentPassword,
  onSuccess,
}: {
  currentPassword: string;
  setCurrentPassword: (value: string) => void;
  onSuccess: () => void;
}) => {
  const { mutateAsync: updatePassword } = usePasswordUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);
  const formMethods = useFormMethods();

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    formMethods.current?.clearErrors();
    try {
      await updatePassword({ password: data.password as string });
      onSuccess();
    } catch {
      formMethods.current?.setGeneralError({
        message: (
          <SettingsErrorAlert i18nKey="my-settings:passwordUpdate.error.message" />
        ),
      });
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      formMethods={formMethods}
      options={{
        defaultValues: { password: '' },
        mode: 'onChange',
      }}
    >
      <PasswordField
        name="password"
        label={t('my-settings:form.password.passwordLabel')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setCurrentPassword(e.target.value)
        }
        required
        autoComplete="new-password"
        hint={t('my-settings:form.password.caseSensitive')}
      />
      <PasswordRequirements value={currentPassword} />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary
          type="submit"
          disabled={!passwordValidator.safeParse(currentPassword).success}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalPassword = ({
  openTrigger,
}: {
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createPasswordUpdateSuccessAlert());
    setIsOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      onClose={() => setCurrentPassword('')}
    >
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        icon="edit-02"
        title={t('my-settings:form.password.title')}
      >
        <PasswordModalForm
          currentPassword={currentPassword}
          setCurrentPassword={setCurrentPassword}
          onSuccess={handleSuccess}
        />
      </DialogContent>
    </Dialog>
  );
};
